import "./styles.scss";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import { useApp } from "context/app";
import { useNewWebsite } from "context/client/new_website";
import { useWebsiteAccessData } from "context/client/website_access_data";

import DetailsCard from "components/DetailsCard";
import PlaceholderPage from "components/PlaceholderPage";
import { AccessDataModal } from "page_components/shared/tasks/modals";

import useResponsive from "libs/helpers/useResponsive";
import countries from "libs/countries.json";

const mainClass = "new-website-content__confirmation";

const Confirmation = () => {
  const { t, i18n } = useTranslation();
  const { min } = useResponsive();

  const { search } = useLocation();
  const { id } = useParams();

  const query = new URLSearchParams(search);

  const { user, websites } = useApp();
  const { website, change_plan_mode } = useNewWebsite();
  const { data: accessData, updateData: updateAccessData } =
    useWebsiteAccessData() || {};

  const [access_data_modal, setAccessDataModal] = useState(false);
  const [is_access_data_filled, setIsAccessDataFilled] = useState(false);

  const website_id = !!change_plan_mode ? id : query.get("id");
  const title = t(
    is_access_data_filled
      ? "All set – your website is fully configured"
      : !!change_plan_mode
        ? "All set, your plan has been changed"
        : "Your website has been added successfully",
  );

  const next_payment_date = website?.subscription?.next_payment
    ? new Date(website.subscription.next_payment * 1000)
        .toLocaleDateString(i18n.language, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, ".")
    : new Date()
        .toLocaleDateString(i18n.language, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, ".");

  useEffect(() => {
    let selected_website = websites?.find((item) => item?._id === website_id);
    const formatted_value = (selected_website?.subscription?.amount / 100)
      ?.toString()
      ?.replace(",", ".");

    window.gtag("event", "purchase", {
      transaction_id: selected_website?._id,
      value: formatted_value,
      email: user?.email,
      currency: selected_website?.subscription?.currency,
      items: [
        {
          item_id: selected_website?.subscription?.price_id,
          item_name: selected_website?.subscription?.name,
          price: formatted_value,
          quantity: 1,
        },
      ],
    });

    window.twq("event", "tw-on3qu-on3r9", {
      value: formatted_value, // use this to pass the value of the conversion (e.g. 5.00)
      currency: selected_website?.subscription?.currency, // use this to pass the currency of the conversion with an ISO 4217 code (e.g. ‘USD’)
      email_address: selected_website?.contact_data?.email, // use this to pass a user’s email address
      phone_number: `${
        countries?.find(
          (item) => item?.code === selected_website?.contact_data?.phone?.code,
        )?.dialling_code || ""
      } ${selected_website?.contact_data?.phone?.number}`,
    });
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <DetailsCard title={min.sm ? t("Confirmation") : undefined} resetOnMobile>
        <div className={mainClass}>
          <div className={`${mainClass}__row`}>
            <div
              className={`${mainClass}__column ${mainClass}__column--info${
                !!change_plan_mode ? ` ${mainClass}__column--full` : ""
              }`}
            >
              <PlaceholderPage
                id="nw-confirmation-1"
                title={title}
                text={
                  is_access_data_filled
                    ? t(
                        "Your website has been fully configured and is now under our care. Detailed information, submitting requests, payments, and much more can be found in the client panel on the left-hand side.",
                      )
                    : t("Your new plan will take effect from") +
                      ` ${next_payment_date}.`
                }
                button={[
                  {
                    label: t("Manage your website"),
                    href: `/websites/${website_id}`,
                    size: "DEFAULT",
                    variant: "gradient-green",
                  },
                ]}
              />
            </div>
            {!is_access_data_filled && !!!change_plan_mode && (
              <div
                className={`${mainClass}__column ${mainClass}__column--access-data`}
              >
                <PlaceholderPage
                  id="nw-confirmation-2"
                  title={t("Website access data")}
                  text={t(
                    "To manage your website and carry out the assigned tasks, we need access credentials for the CMS, server, and database. You can provide them at any time in the panel. If you're not sure what they are, we’ll get in touch with you and help you obtain them.",
                  )}
                  button={{
                    label: t("Add access data"),
                    size: "DEFAULT",
                    variant: "transparent-dark",
                  }}
                  color="green"
                  event={() => setAccessDataModal(true)}
                />
              </div>
            )}
          </div>
        </div>
      </DetailsCard>

      {!!!change_plan_mode && (
        <AccessDataModal
          data={accessData}
          updateData={updateAccessData}
          isOpen={access_data_modal}
          onClose={() => setAccessDataModal(false)}
          onSubmit={() => setIsAccessDataFilled(true)}
        />
      )}
    </>
  );
};

export default Confirmation;
